import { create } from 'zustand';

import { FILTER_GROUP_TYPES } from '@/shared/api/openmetadata';

import { getFilteredOptions } from '@/entities/discovery/api';

import type { FilterGroupType, FilterOption, FilterState, QuickFilter, SelectedFilter } from './types';

type FilterCriteria = {
  dataSource?: string[];
  assetType?: string[];
  domain?: string[];
  owner?: string[];
  tier?: string[];
  tags?: string[];
  certification?: string[];
  quickFilter?: QuickFilter;
};

type FilterStore = {
  filters: FilterState;
  isLoading: boolean;

  initialOptions: Record<FilterGroupType, FilterOption[]>;
  filteredOptions: Record<FilterGroupType, FilterOption[]>;
  isLoadingOptions: Record<FilterGroupType, boolean>;

  lastChangeSource?: 'topBar' | 'sidebar' | 'tree';

  updateDataSourceFilter: (values: string[], source?: 'topBar' | 'sidebar' | 'tree') => void;
  updateAssetTypeFilter: (values: string[], source?: 'topBar' | 'sidebar' | 'tree') => void;
  updateDomainFilter: (values: string[]) => void;
  updateOwnerFilter: (values: string[]) => void;
  updateTierFilter: (values: string[]) => void;
  updateTagsFilter: (values: string[]) => void;
  updateCertificationFilter: (values: string[]) => void;
  setTagsOperator: (operator: 'AND' | 'OR') => void;
  updateQuickFilter: (quickFilter: QuickFilter | undefined, source?: 'topBar' | 'sidebar' | 'tree') => void;

  setInitialOptions: (groupType: FilterGroupType, options: FilterOption[]) => void;
  updateFilteredOptions: (groupType: FilterGroupType, options: FilterOption[]) => void;
  setLoadingOptions: (groupType: FilterGroupType, isLoading: boolean) => void;
  updateOptionsForGroup: (targetGroup: FilterGroupType, excludeGroup?: FilterGroupType) => void;
  setFiltersAndUpdateOptions: (filters: Partial<FilterState>) => void;

  clearAllFilters: () => void;
  clearFilterGroup: (groupType: keyof FilterState) => void;

  getFilterCriteria: () => FilterCriteria;
  getSelectedFilters: () => SelectedFilter[];

  getActiveFilterCount: () => number;
};

const initialFilterState: FilterState = {
  dataSource: [],
  assetType: [],
  domain: [],
  owner: [],
  tier: [],
  tags: [],
  certification: [],
  tagsOperator: 'OR',
  quickFilter: undefined,
};

export const useFilterStore = create<FilterStore>((set, get) => ({
  filters: initialFilterState,
  isLoading: false,

  lastChangeSource: undefined,

  initialOptions: {
    [FILTER_GROUP_TYPES.DATA_SOURCE]: [],
    [FILTER_GROUP_TYPES.ASSET_TYPE]: [],
    [FILTER_GROUP_TYPES.DOMAIN]: [],
    [FILTER_GROUP_TYPES.OWNER]: [],
    [FILTER_GROUP_TYPES.TIER]: [],
    [FILTER_GROUP_TYPES.TAGS]: [],
    [FILTER_GROUP_TYPES.CERTIFICATION]: [],
  },

  filteredOptions: {
    [FILTER_GROUP_TYPES.DATA_SOURCE]: [],
    [FILTER_GROUP_TYPES.ASSET_TYPE]: [],
    [FILTER_GROUP_TYPES.DOMAIN]: [],
    [FILTER_GROUP_TYPES.OWNER]: [],
    [FILTER_GROUP_TYPES.TIER]: [],
    [FILTER_GROUP_TYPES.TAGS]: [],
    [FILTER_GROUP_TYPES.CERTIFICATION]: [],
  },

  isLoadingOptions: {
    [FILTER_GROUP_TYPES.DATA_SOURCE]: false,
    [FILTER_GROUP_TYPES.ASSET_TYPE]: false,
    [FILTER_GROUP_TYPES.DOMAIN]: false,
    [FILTER_GROUP_TYPES.OWNER]: false,
    [FILTER_GROUP_TYPES.TIER]: false,
    [FILTER_GROUP_TYPES.TAGS]: false,
    [FILTER_GROUP_TYPES.CERTIFICATION]: false,
  },

  updateDataSourceFilter: (values, source = 'sidebar') => {
    set((state) => ({
      filters: { ...state.filters, dataSource: values },
      lastChangeSource: source,
    }));

    const filterDependencies: FilterGroupType[] = [
      FILTER_GROUP_TYPES.ASSET_TYPE,
      FILTER_GROUP_TYPES.OWNER,
      FILTER_GROUP_TYPES.TIER,
      FILTER_GROUP_TYPES.TAGS,
      FILTER_GROUP_TYPES.CERTIFICATION,
    ];
    filterDependencies.forEach((targetGroup) => {
      get().updateOptionsForGroup(targetGroup, FILTER_GROUP_TYPES.DATA_SOURCE);
    });
  },

  updateAssetTypeFilter: (values, source = 'sidebar') => {
    set((state) => ({
      filters: { ...state.filters, assetType: values },
      lastChangeSource: source,
    }));

    const filterDependencies: FilterGroupType[] = [
      FILTER_GROUP_TYPES.OWNER,
      FILTER_GROUP_TYPES.TIER,
      FILTER_GROUP_TYPES.TAGS,
      FILTER_GROUP_TYPES.CERTIFICATION,
    ];
    filterDependencies.forEach((targetGroup) => {
      get().updateOptionsForGroup(targetGroup, FILTER_GROUP_TYPES.ASSET_TYPE);
    });
  },

  updateDomainFilter: (values) => {
    set((state) => ({
      filters: { ...state.filters, domain: values },
    }));

    const filterDependencies: FilterGroupType[] = [
      FILTER_GROUP_TYPES.OWNER,
      FILTER_GROUP_TYPES.TIER,
      FILTER_GROUP_TYPES.TAGS,
      FILTER_GROUP_TYPES.CERTIFICATION,
    ];
    filterDependencies.forEach((targetGroup) => {
      get().updateOptionsForGroup(targetGroup, FILTER_GROUP_TYPES.DOMAIN);
    });
  },

  updateOwnerFilter: (values) => {
    set((state) => ({
      filters: { ...state.filters, owner: values },
    }));

    const filterDependencies: FilterGroupType[] = [FILTER_GROUP_TYPES.TIER, FILTER_GROUP_TYPES.TAGS];
    filterDependencies.forEach((targetGroup) => {
      get().updateOptionsForGroup(targetGroup, FILTER_GROUP_TYPES.OWNER);
    });
  },

  updateTierFilter: (values) => {
    set((state) => ({
      filters: { ...state.filters, tier: values },
    }));

    get().updateOptionsForGroup(FILTER_GROUP_TYPES.TAGS, FILTER_GROUP_TYPES.TIER);
  },

  updateTagsFilter: (values) => {
    set((state) => ({
      filters: { ...state.filters, tags: values },
    }));
  },

  updateCertificationFilter: (values) => {
    set((state) => ({
      filters: { ...state.filters, certification: values },
    }));
  },

  setTagsOperator: (operator) => {
    set((state) => ({
      filters: { ...state.filters, tagsOperator: operator },
    }));
  },

  updateQuickFilter: (quickFilter, source = 'sidebar') => {
    set((state) => ({
      filters: { ...state.filters, quickFilter },
      lastChangeSource: source,
    }));
  },

  setInitialOptions: (groupType, options) => {
    set((state) => ({
      initialOptions: {
        ...state.initialOptions,
        [groupType]: options,
      },
      filteredOptions: {
        ...state.filteredOptions,
        [groupType]: options,
      },
    }));
  },

  updateFilteredOptions: (groupType, options) => {
    set((state) => ({
      filteredOptions: {
        ...state.filteredOptions,
        [groupType]: options,
      },
    }));
  },

  setLoadingOptions: (groupType, isLoading) => {
    set((state) => ({
      isLoadingOptions: {
        ...state.isLoadingOptions,
        [groupType]: isLoading,
      },
    }));
  },

  updateOptionsForGroup: (targetGroup, excludeGroup) => {
    const { initialOptions, filters } = get();

    const relevantFilters = { ...filters };
    if (excludeGroup) {
      const FILTER_CLEAR_HANDLERS: Partial<Record<FilterGroupType, () => void>> = {
        [FILTER_GROUP_TYPES.DATA_SOURCE]: () => {
          relevantFilters.dataSource = [];
        },
        [FILTER_GROUP_TYPES.ASSET_TYPE]: () => {
          relevantFilters.assetType = [];
        },
        [FILTER_GROUP_TYPES.OWNER]: () => {
          relevantFilters.owner = [];
        },
        [FILTER_GROUP_TYPES.TIER]: () => {
          relevantFilters.tier = [];
        },
        [FILTER_GROUP_TYPES.TAGS]: () => {
          relevantFilters.tags = [];
        },
      };

      FILTER_CLEAR_HANDLERS[excludeGroup]?.();
    }

    const hasActiveFilters = Object.values(relevantFilters).some(
      (values) => Array.isArray(values) && values.length > 0
    );

    if (!hasActiveFilters) {
      get().updateFilteredOptions(targetGroup, initialOptions[targetGroup]);
      return;
    }

    const getFilteredOptionsAsync = async () => {
      get().setLoadingOptions(targetGroup, true);
      try {
        const filteredOptions = await getFilteredOptions(targetGroup, relevantFilters, excludeGroup);
        get().updateFilteredOptions(targetGroup, filteredOptions);
      } catch (error) {
        console.error(`Failed to update filtered options for ${targetGroup}:`, error);
        get().updateFilteredOptions(targetGroup, initialOptions[targetGroup]);
      } finally {
        get().setLoadingOptions(targetGroup, false);
      }
    };

    getFilteredOptionsAsync();
  },

  setFiltersAndUpdateOptions: (filters) => {
    set((state) => ({
      filters: {
        ...state.filters,
        ...filters,
      },
    }));

    const hasActiveFilters = Object.values(filters).some((filterArray) =>
      Array.isArray(filterArray) ? filterArray.length > 0 : Boolean(filterArray)
    );

    if (hasActiveFilters) {
      setTimeout(() => {
        const allGroupTypes = Object.keys(get().initialOptions) as FilterGroupType[];
        allGroupTypes.forEach((targetGroup) => {
          get().updateOptionsForGroup(targetGroup);
        });
      }, 50);
    }
  },

  clearAllFilters: () => {
    const { initialOptions } = get();

    set({
      filters: initialFilterState,
      filteredOptions: { ...initialOptions },
      lastChangeSource: undefined,
    });
  },

  clearFilterGroup: (groupType) => {
    set((state) => {
      const newFilters = { ...state.filters };

      const FILTER_GROUP_CLEAR_HANDLERS: Record<string, () => void> = {
        [FILTER_GROUP_TYPES.DATA_SOURCE]: () => {
          newFilters.dataSource = [];
        },
        [FILTER_GROUP_TYPES.ASSET_TYPE]: () => {
          newFilters.assetType = [];
        },
        [FILTER_GROUP_TYPES.OWNER]: () => {
          newFilters.owner = [];
        },
        [FILTER_GROUP_TYPES.TIER]: () => {
          newFilters.tier = [];
        },
        [FILTER_GROUP_TYPES.TAGS]: () => {
          newFilters.tags = [];
        },
        [FILTER_GROUP_TYPES.CERTIFICATION]: () => {
          newFilters.certification = [];
        },
      };

      FILTER_GROUP_CLEAR_HANDLERS[groupType]?.();

      return { filters: newFilters };
    });

    const allGroupTypes = Object.keys(get().initialOptions) as FilterGroupType[];
    allGroupTypes.forEach((targetGroup) => {
      get().updateOptionsForGroup(targetGroup);
    });
  },

  getFilterCriteria: () => {
    const { filters } = get();

    return {
      dataSource: filters.dataSource.length > 0 ? filters.dataSource : undefined,
      assetType: filters.assetType.length > 0 ? filters.assetType : undefined,
      domain: filters.domain.length > 0 ? filters.domain : undefined,
      owner: filters.owner.length > 0 ? filters.owner : undefined,
      tier: filters.tier.length > 0 ? filters.tier : undefined,
      tags: filters.tags.length > 0 ? filters.tags : undefined,
      certification: filters.certification.length > 0 ? filters.certification : undefined,
      quickFilter: filters.quickFilter,
    };
  },

  getSelectedFilters: () => {
    const { filters } = get();
    const selectedFilters: SelectedFilter[] = [];

    if (filters.dataSource.length > 0) {
      selectedFilters.push({
        groupType: FILTER_GROUP_TYPES.DATA_SOURCE,
        values: filters.dataSource,
      });
    }

    if (filters.assetType.length > 0) {
      selectedFilters.push({
        groupType: FILTER_GROUP_TYPES.ASSET_TYPE,
        values: filters.assetType,
      });
    }

    if (filters.domain.length > 0) {
      selectedFilters.push({
        groupType: FILTER_GROUP_TYPES.DOMAIN,
        values: filters.domain,
      });
    }

    if (filters.owner.length > 0) {
      selectedFilters.push({
        groupType: FILTER_GROUP_TYPES.OWNER,
        values: filters.owner,
      });
    }

    if (filters.tier.length > 0) {
      selectedFilters.push({
        groupType: FILTER_GROUP_TYPES.TIER,
        values: filters.tier,
      });
    }

    if (filters.tags.length > 0) {
      selectedFilters.push({
        groupType: FILTER_GROUP_TYPES.TAGS,
        values: filters.tags,
      });
    }

    if (filters.certification.length > 0) {
      selectedFilters.push({
        groupType: FILTER_GROUP_TYPES.CERTIFICATION,
        values: filters.certification,
      });
    }

    return selectedFilters;
  },

  getActiveFilterCount: () => {
    const { filters } = get();
    let count = 0;

    if (filters.dataSource.length > 0) count += 1;
    if (filters.assetType.length > 0) count += 1;
    if (filters.domain.length > 0) count += 1;
    if (filters.owner.length > 0) count += 1;
    if (filters.tier.length > 0) count += 1;
    if (filters.tags.length > 0) count += 1;
    if (filters.certification.length > 0) count += 1;
    if (filters.quickFilter && Object.keys(filters.quickFilter).length > 0) count += 1;

    return count;
  },
}));
